import type {
  RecurringUsageRule,
  StayPlanFinancialStatus,
  StayPlanKind,
  StayPlanModule,
  StayPlanPaymentStatus,
} from "@/lib/daycare-hotel/stayPlans";
import type { StayPlanContract } from "@/hooks/useStayPlanContracts";

export interface PlanUsageEvent {
  source: string;
  units: number;
  usedOn: string;
}

export interface PlanChargeEvent {
  dueOn: string;
  periodEnd: string;
  periodStart: string;
  saleId: string;
  saleStatus: string;
  total: number;
}

export interface PlanRow {
  contractId: string;
  contract: StayPlanContract;
  origin: StayReportOrigin;
  petId: string;
  petName: string;
  ownerName: string;
  planName: string;
  kind: StayPlanKind;
  module: StayPlanModule;
  type: StayReportType;
  status: string;
  financialStatus: StayPlanFinancialStatus;
  nextDueOn: string | null;
  /** Saldo previsto do ciclo; `null` em plano ilimitado ou `per_week` (ver `usageRule`). */
  balance: number | null;
  carryoverUnits: number;
  usageRule: RecurringUsageRule | null;
  /** Venda em aberto a receber deste contrato, quando existe. */
  openChargeSaleId: string | null;
  /** Eixo de dinheiro: pago, agendado, aguardando, vencido ou sem cobrança. */
  paymentStatus: StayPlanPaymentStatus;
  /** Valor da cobrança em aberto; `null` quando não há o que cobrar. */
  openAmount: number | null;
  /** Vencimento da cobrança em aberto; `null` quando não há o que cobrar. */
  dueOn: string | null;
  /** Período do ciclo corrente; `null` em pacote de diárias. */
  cycleStart: string | null;
  cycleEnd: string | null;
  /** Unidades consumidas no ciclo; `null` quando o plano não tem cota de ciclo. */
  usedInCycle: number | null;
  quota: number | null;
  startsOn: string;
  usageEvents: PlanUsageEvent[];
  chargeEvents: PlanChargeEvent[];
}

export type StayReportOrigin = "plan" | "ad_hoc";
export type StayReportType = "daycare" | "hotel" | "daycare_overnight";

export interface PlanReportRange {
  from: string;
  to: string;
}

export type PlanAttentionReason =
  | "overdue"
  | "balance_zero"
  | "due_today"
  | "due_soon"
  | "balance_low";

export type PlanReportView = "attention" | "all";
export type PlanTypeFilter = "all" | StayReportType;
export type PlanPaymentFilter = "all" | "paid" | "receivable" | "overdue" | "none";
export type PlanConsumptionFilter = "all" | "zero" | "low" | "available" | "unmetered";
export type PlanStatusFilter = "all" | "active" | "ended";

export interface PlanReportRow extends PlanRow {
  attentionPriority: number;
  attentionReason: PlanAttentionReason | null;
  usageInPeriod: number;
}

export interface PlanReportFilters {
  consumption: PlanConsumptionFilter;
  type: PlanTypeFilter;
  payment: PlanPaymentFilter;
  search: string;
  status: PlanStatusFilter;
  view: PlanReportView;
}

export interface PlanReportSummary {
  attention: number;
  dueSoon: number;
  lowBalance: number;
  overdue: number;
  receivableAmount: number;
  total: number;
}

export const DEFAULT_PLAN_REPORT_FILTERS: PlanReportFilters = {
  consumption: "all",
  type: "all",
  payment: "all",
  search: "",
  status: "all",
  view: "attention",
};

export type StayPlanFilter =
  | "all" | "regular" | "due_soon" | "overdue" | "ended"
  | "paid" | "receivable";

const RECEIVABLE: StayPlanPaymentStatus[] = ["scheduled", "awaiting", "overdue"];

const overlaps = (start: string | null, end: string | null, range: PlanReportRange) =>
  Boolean(start && end && start <= range.to && end >= range.from);

const normalizeSearch = (value: string) =>
  value.toLocaleLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

function attentionFor(row: PlanRow): Pick<PlanReportRow, "attentionPriority" | "attentionReason"> {
  if (row.status !== "active") return { attentionPriority: 0, attentionReason: null };
  if (row.paymentStatus === "overdue") return { attentionPriority: 50, attentionReason: "overdue" };

  const metered = row.usageRule !== "per_week";
  if (metered && row.balance === 0) return { attentionPriority: 40, attentionReason: "balance_zero" };
  if (row.financialStatus === "due_today") return { attentionPriority: 30, attentionReason: "due_today" };
  if (row.financialStatus === "due_soon") return { attentionPriority: 20, attentionReason: "due_soon" };
  if (metered && row.balance === 1) return { attentionPriority: 10, attentionReason: "balance_low" };

  return { attentionPriority: 0, attentionReason: null };
}

export function buildPlanReportRows(rows: PlanRow[], range: PlanReportRange): PlanReportRow[] {
  return rows
    .filter((row) =>
      (row.startsOn >= range.from && row.startsOn <= range.to) ||
      overlaps(row.cycleStart, row.cycleEnd, range) ||
      row.chargeEvents.some((charge) => overlaps(charge.periodStart, charge.periodEnd, range)) ||
      row.usageEvents.some((usage) => usage.usedOn >= range.from && usage.usedOn <= range.to),
    )
    .map((row) => ({
      ...row,
      ...attentionFor(row),
      usageInPeriod: row.usageEvents
        .filter((usage) => usage.source === "checkin" && usage.usedOn >= range.from && usage.usedOn <= range.to)
        .reduce((sum, usage) => sum + usage.units, 0),
    }))
    .sort((a, b) => b.attentionPriority - a.attentionPriority || a.petName.localeCompare(b.petName));
}

export function filterPlanReportRows(rows: PlanReportRow[], filters: PlanReportFilters): PlanReportRow[] {
  const needle = normalizeSearch(filters.search.trim());

  return rows.filter((row) => {
    if (filters.view === "attention" && !row.attentionReason) return false;
    if (filters.type !== "all" && row.type !== filters.type) return false;
    if (filters.status === "active" && row.status !== "active") return false;
    if (filters.status === "ended" && row.status === "active") return false;
    if (filters.payment === "paid" && row.paymentStatus !== "paid") return false;
    if (filters.payment === "receivable" && !RECEIVABLE.includes(row.paymentStatus)) return false;
    if (filters.payment === "overdue" && row.paymentStatus !== "overdue") return false;
    if (filters.payment === "none" && row.paymentStatus !== "none") return false;
    if (filters.consumption === "zero" && row.balance !== 0) return false;
    if (filters.consumption === "low" && row.balance !== 1) return false;
    if (filters.consumption === "available" && !(row.balance !== null && row.balance > 1)) return false;
    if (filters.consumption === "unmetered" && row.usageRule !== "per_week") return false;

    return !needle || normalizeSearch(`${row.petName} ${row.ownerName} ${row.planName}`).includes(needle);
  });
}

export function summarizePlanReportRows(rows: PlanReportRow[]): PlanReportSummary {
  return rows.reduce<PlanReportSummary>((summary, row) => ({
    total: summary.total + 1,
    attention: summary.attention + (row.attentionReason ? 1 : 0),
    overdue: summary.overdue + (row.status === "active" && row.paymentStatus === "overdue" ? 1 : 0),
    dueSoon: summary.dueSoon + (row.status === "active" && (row.financialStatus === "due_soon" || row.financialStatus === "due_today") ? 1 : 0),
    lowBalance: summary.lowBalance + (row.status === "active" && row.balance !== null && row.balance <= 1 && row.usageRule !== "per_week" ? 1 : 0),
    receivableAmount: summary.receivableAmount + (row.status === "active" && RECEIVABLE.includes(row.paymentStatus) ? row.openAmount ?? 0 : 0),
  }), {
    total: 0,
    attention: 0,
    overdue: 0,
    dueSoon: 0,
    lowBalance: 0,
    receivableAmount: 0,
  });
}

export function filterPlanRows(rows: PlanRow[], filter: StayPlanFilter): PlanRow[] {
  if (filter === "all") return rows;
  if (filter === "ended") return rows.filter((row) => row.status !== "active");

  const active = rows.filter((row) => row.status === "active");
  if (filter === "paid") return active.filter((row) => row.paymentStatus === "paid");
  if (filter === "receivable") return active.filter((row) => RECEIVABLE.includes(row.paymentStatus));
  if (filter === "due_soon") {
    return active.filter((row) => row.financialStatus === "due_soon" || row.financialStatus === "due_today");
  }
  return active.filter((row) => row.financialStatus === filter);
}
